import { MouseEventHandler, useCallback, useState, useMemo } from 'react';
import { DateRange } from './DateRangePicker.types';
import useLocale from 'hooks/useLocale';
import { useYodaField, UseYodaFieldProps } from 'services/yodaForm/hooks/useYodaField';

const useDateRangePicker = ({ name, ...props }: UseYodaFieldProps) => {
  const { locale } = useLocale();
  const {
    onChangeFieldDateRange,
    fieldValue,
    fieldShowError,
    fieldErrorMessage,
    setValue,
    setFormIsDirty,
  } = useYodaField({ name, ...props });

  const [calendarOpen, setCalendarOpen] = useState(false);

  const hideCalendar = useCallback(() => setCalendarOpen(false), []);
  const showCalendar = useCallback(() => setCalendarOpen(true), []);

  const handleChange = useCallback((date: DateRange | null) => {
    if (date === null || date[0] === null) {
      return;
    }
    onChangeFieldDateRange(date);
    setFormIsDirty(true);
  }, [onChangeFieldDateRange, setFormIsDirty]);

  const handleClearButtonClick: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.stopPropagation();
    if (fieldValue && fieldValue.length > 0) {
      setValue(name, [null, null]);
    }
  }, [fieldValue, name, setValue]);

  const clearButtonDisabled = useMemo(() => (!fieldValue || fieldValue.length <= 1 || fieldValue[1] == null), [fieldValue]);

  return {
    locale,
    fieldValue,
    fieldShowError,
    fieldErrorMessage,
    calendarOpen,
    hideCalendar,
    showCalendar,
    handleChange,
    handleClearButtonClick,
    clearButtonDisabled,
  };
};

export default useDateRangePicker;
